import { useMemo, useState } from 'react';
import {
  ChevronRight,
  Copy,
  Download,
  FilterX,
  RefreshCw,
  Settings2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useColumnState } from '@/hooks/useColumnState';
import { copyCSVToClipboard, downloadCSV, toCSV, type CSVColumn } from '@/lib/csv';
import { cn } from '@/lib/utils';
import { DataTable, type DataTableColumn } from '@/shared/components/data-table';
import { Pagination } from '@/shared/components/Pagination';
import type {
  TrainingAssignment,
  TrainingFilters,
} from '../../types/training.types';
import { trainingTimeRemaining } from '../../utils/training';
import {
  TrainingComplianceBadge,
  TrainingStatusBadge,
} from './TrainingStatusBadge';

type TrainingColumn = DataTableColumn<TrainingAssignment> & {
  defaultVisible?: boolean;
};

const CSV_COLUMNS: CSVColumn<TrainingAssignment>[] = [
  { header: 'Employee', value: (row) => row.employeeName || '' },
  { header: 'Control ID', value: (row) => row.documentControlCode },
  { header: 'Document', value: (row) => row.documentName },
  { header: 'Version', value: (row) => row.documentVersion },
  { header: 'Document group', value: (row) => row.documentGroup },
  { header: 'Assigned at', value: (row) => row.assignedAt },
  { header: 'Due at', value: (row) => row.dueAt },
  { header: 'Workflow status', value: (row) => row.status },
  { header: 'Compliance', value: (row) => row.complianceState },
  { header: 'Attempts used', value: (row) => row.attemptsUsed },
  { header: 'Completed at', value: (row) => row.completedAt || '' },
];

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-CA', {
    dateStyle: 'medium',
    timeZone: 'America/Montreal',
  }).format(new Date(value));
}

interface TrainingAssignmentsTableProps {
  assignments: TrainingAssignment[];
  total: number;
  page: number;
  pageLength: number;
  sortKey: TrainingFilters['sortKey'];
  sortDirection: 1 | -1;
  columnFilters: Record<string, string>;
  status: TrainingFilters['status'];
  complianceMode?: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  onColumnFilterChange: (key: string, value: string) => void;
  onClearFilters: () => void;
  onSort: (key: TrainingFilters['sortKey']) => void;
  onPageChange: (page: number) => void;
  onRowClick: (assignment: TrainingAssignment) => void;
  onRefresh: () => void;
  onExportAll: () => Promise<TrainingAssignment[]>;
}

export function TrainingAssignmentsTable({
  assignments,
  total,
  page,
  pageLength,
  sortKey,
  sortDirection,
  columnFilters,
  status,
  complianceMode = false,
  isLoading,
  isRefreshing,
  onColumnFilterChange,
  onClearFilters,
  onSort,
  onPageChange,
  onRowClick,
  onRefresh,
  onExportAll,
}: TrainingAssignmentsTableProps) {
  const [isExportingAll, setIsExportingAll] = useState(false);
  const columns = useMemo<TrainingColumn[]>(() => [
    ...(complianceMode
      ? [{
        key: 'employee',
        label: 'Employee',
        filterType: 'text' as const,
        isPrimarySearch: true,
        searchPlaceholder: 'Search employee or SOP…',
        getValue: (row: TrainingAssignment) => row.employeeName || '',
        render: (row: TrainingAssignment) => (
          <div>
            <div className="font-medium text-slate-900">{row.employeeName || 'Unknown employee'}</div>
            <div className="text-[11px] text-slate-500">{row.employeeEmail || '—'}</div>
          </div>
        ),
      }]
      : []),
    {
      key: 'documentControlCode',
      label: 'Control ID',
      sortKey: 'documentControlCode',
      filterType: 'text',
      isPrimarySearch: !complianceMode,
      searchPlaceholder: 'Search SOP or document…',
      getValue: (row) => row.documentControlCode,
      render: (row) => (
        <span className="font-mono text-xs font-semibold text-slate-900">
          {row.documentControlCode}
        </span>
      ),
    },
    {
      key: 'documentName',
      label: 'Controlled document',
      sortKey: 'documentName',
      filterType: 'text',
      getValue: (row) => row.documentName,
      render: (row) => (
        <div className="max-w-sm">
          <div className="truncate font-medium text-slate-900">{row.documentName}</div>
          <div className="text-[11px] text-slate-500">Version {row.documentVersion}</div>
        </div>
      ),
    },
    {
      key: 'documentGroup',
      label: 'Group',
      filterType: 'text',
      getValue: (row) => row.documentGroup,
    },
    {
      key: 'assignedAt',
      label: 'Assigned',
      sortKey: 'assignedAt',
      noFilter: true,
      defaultVisible: complianceMode,
      getValue: (row) => row.assignedAt,
      render: (row) => formatDate(row.assignedAt),
    },
    {
      key: 'dueAt',
      label: 'Due',
      sortKey: 'dueAt',
      noFilter: true,
      getValue: (row) => row.dueAt,
      render: (row) => (
        <div>
          <div className="font-medium tabular-nums">{formatDate(row.dueAt)}</div>
          <div className={cn(
            'text-[11px]',
            row.complianceState === 'red' ? 'font-medium text-rose-700' : 'text-slate-500',
          )}>
            {trainingTimeRemaining(row.dueAt)}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Workflow',
      sortKey: 'status',
      noFilter: true,
      getValue: (row) => row.status,
      render: (row) => <TrainingStatusBadge status={row.status} />,
    },
    {
      key: 'complianceState',
      label: 'Qualification',
      noFilter: true,
      getValue: (row) => row.complianceState,
      render: (row) => <TrainingComplianceBadge state={row.complianceState} />,
    },
    {
      key: 'assessment',
      label: 'Assessment',
      noFilter: true,
      defaultVisible: !complianceMode,
      getValue: (row) => row.quizRequired ? `${row.attemptsUsed}/${row.maxAttempts}` : 'Not required',
      render: (row) => row.quizRequired
        ? <span className="tabular-nums">{row.attemptsUsed}/{row.maxAttempts} used</span>
        : <span className="text-slate-400">Not required</span>,
    },
    {
      key: 'actions',
      label: '',
      noFilter: true,
      getValue: () => '',
      render: () => <ChevronRight className="ml-auto h-4 w-4 text-slate-400" />,
    },
  ], [complianceMode]);

  const columnState = useColumnState({
    columns,
    storageKeys: {
      visibility: complianceMode
        ? 'atek-eqms-training-compliance-columns'
        : 'atek-eqms-training-assignment-columns',
      order: complianceMode
        ? 'atek-eqms-training-compliance-column-order'
        : 'atek-eqms-training-assignment-column-order',
      widths: complianceMode
        ? 'atek-eqms-training-compliance-column-widths'
        : 'atek-eqms-training-assignment-column-widths',
    },
  });
  const hasFilters = Object.values(columnFilters).some(Boolean) || (status && status !== 'all');

  const exportRows = (rows: TrainingAssignment[], suffix: string) => {
    downloadCSV(`eqms-training-${suffix}-${new Date().toISOString().slice(0, 10)}.csv`, toCSV(rows, CSV_COLUMNS));
    toast.success(`Exported ${rows.length} training records`);
  };

  const exportAll = async () => {
    try {
      setIsExportingAll(true);
      exportRows(await onExportAll(), 'all');
    } catch {
      toast.error('Could not export all training records');
    } finally {
      setIsExportingAll(false);
    }
  };

  const copyPage = async () => {
    try {
      await copyCSVToClipboard(toCSV(assignments, CSV_COLUMNS));
      toast.success(`Copied ${assignments.length} training records`);
    } catch {
      toast.error('Could not copy training records');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-500">
          {assignments.length} on this page · {total} total
        </div>
        <div className="flex flex-wrap gap-2">
          {hasFilters && (
            <Button variant="outline" size="sm" onClick={onClearFilters}>
              <FilterX className="mr-1 h-4 w-4" /> Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={copyPage}>
            <Copy className="mr-1 h-4 w-4" /> Copy
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isExportingAll}>
                <Download className="mr-1 h-4 w-4" />
                {isExportingAll ? 'Exporting…' : 'CSV'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => exportRows(assignments, 'page')}>
                Export page ({assignments.length})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportAll}>Export all ({total})</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn('mr-1 h-4 w-4', isRefreshing && 'animate-spin')} /> Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="mr-1 h-4 w-4" /> Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columnState.orderedColumns
                .filter((column) => column.key !== 'actions')
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.key}
                    checked={columnState.isVisible(column.key)}
                    onCheckedChange={() => columnState.toggleColumn(column.key)}
                  >
                    {column.label}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DataTable
        items={assignments}
        columns={columnState.visibleColumns}
        ctx={undefined}
        columnFilters={columnFilters}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={(key) => onSort(key as TrainingFilters['sortKey'])}
        onSetColumnFilter={onColumnFilterChange}
        onRowClick={onRowClick}
        isLoading={isLoading}
        emptyState="No training records match these filters."
        className="border-slate-200 shadow-sm"
      />

      <Pagination
        page={page + 1}
        pageSize={pageLength}
        total={total}
        onPageChange={(nextPage) => onPageChange(nextPage - 1)}
      />
    </div>
  );
}
